import React, { FC, useState } from "react";
import Drawer from "shared/Drawer/Drawer";
import { useCartDataProvider } from "context/CartProductContext";
import { useNavigate } from "react-router-dom";
import { Product } from "data/data";
import CartItem from "./CartItem";
import CartSuggestions from "./CartSuggestions";
import { ShoppingBagIcon } from "@heroicons/react/24/outline";
import Cookies from "js-cookie";

const CartDrawer: FC = () => {
  const {
    Products,
    dispatch,
    itemCount,
    subtotal,
    isCartDrawerOpen,
    closeCartDrawer,
  } = useCartDataProvider();
  const navigate = useNavigate();
  const merchantname = Cookies.get("merchantname");
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleCheckout = () => {
    closeCartDrawer();
    const userDataString = Cookies.get("userDataaInfo");
    const SignupData = Cookies.get("SignupData");
    if (userDataString || SignupData) {
      navigate(`/${merchantname}/checkout`);
    } else {
      navigate(`/${merchantname}/userdetails`);
    }
  };

  const handleClearCart = () => {
    dispatch({ type: "CLEAR_CART" });
    setShowClearConfirm(false);
  };

  const handleEditItem = (item: Product) => {
    closeCartDrawer();
    navigate(`/${merchantname}/product-detail/${item.id}`, {
      state: {
        modifierChecked: item.modifierChecked || {},
        nestedModifierSelections: (item as any).nestedModifierSelections || {},
        cartItemId: item.cartItemId,
        quantity: item.quantity,
        note: item.note,
      },
    });
  };

  return (
    <Drawer
      isOpen={isCartDrawerOpen}
      onClose={closeCartDrawer}
      title={`Your Order (${itemCount} ${itemCount === 1 ? "item" : "items"})`}
    >
      {Products.length === 0 ? (
        /* Empty cart state */
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <ShoppingBagIcon className="w-10 h-10 text-slate-300 dark:text-slate-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
            Your cart is empty
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            Browse the menu to add items
          </p>
          <button
            onClick={closeCartDrawer}
            className="px-6 py-2.5 bg-primary-500 dark:bg-primary-400 text-white dark:text-slate-900 rounded-full font-semibold text-sm hover:bg-primary-600 dark:hover:bg-primary-500 transition-colors"
          >
            Browse Menu
          </button>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Cart items */}
          <div className="flex-1 overflow-y-auto px-4">
            {Products.map((item: Product, index: number) => (
              <CartItem
                key={item.cartItemId || index}
                item={item}
                onEdit={handleEditItem}
              />
            ))}

            {/* Upsell suggestions */}
            <CartSuggestions />

            {/* Clear cart link */}
            <div className="py-4">
              {!showClearConfirm ? (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                >
                  Clear Cart
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Clear all items?
                  </span>
                  <button
                    onClick={handleClearCart}
                    className="text-sm font-semibold text-red-500 dark:text-red-400"
                  >
                    Yes, clear
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="text-sm font-semibold text-slate-500 dark:text-slate-400"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sticky checkout footer */}
          <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-900">
            <div className="flex justify-between mb-3">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Subtotal
              </span>
              <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
              Taxes calculated at checkout
            </p>
            <button
              onClick={handleCheckout}
              className="w-full py-3.5 bg-accent-500 dark:bg-accent-400 text-white dark:text-slate-900 rounded-xl font-bold text-base hover:bg-accent-600 dark:hover:bg-accent-500 transition-colors shadow-md"
            >
              Checkout - ${subtotal.toFixed(2)}
            </button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default CartDrawer;
